// CreatorSelectionCard.js - SIMPLIFIED & OPTIMIZED
import React, { memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
  Checkbox,
  Stack,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { alpha, useTheme, styled } from '@mui/material/styles';

const GlassCard = styled(Card)(({ theme, selected, disabled }) => ({
  borderRadius: 16,
  border: '2px solid',
  borderColor: selected 
    ? theme.palette.primary.main
    : disabled
      ? alpha(theme.palette.divider, 0.3)
      : alpha(theme.palette.divider, 0.5),
  background: selected
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`
    : theme.palette.background.paper,
  transition: 'all 0.2s ease',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  '&:hover': !disabled && {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
}));

function CreatorSelectionCard({ creator, selected, onToggle, disabled }) {
  const theme = useTheme();
  
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const totalFollowers = Object.values(creator.social_stats || {}).reduce((sum, val) => sum + val, 0);

  return (
    <GlassCard selected={selected} disabled={disabled} onClick={() => !disabled && onToggle()}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Checkbox
            checked={selected}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
            onChange={onToggle}
            sx={{ 
              '& .MuiSvgIcon-root': { fontSize: 26 },
              '&.Mui-checked': {
                color: theme.palette.primary.main,
              }
            }}
          />
          
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={creator.pfphref}
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                border: `2px solid ${selected ? theme.palette.primary.main : 'transparent'}`,
                transition: 'all 0.2s ease',
              }}
            >
              {creator.display_name?.[0] || creator.creator?.[0] || '?'}
            </Avatar>
            {selected && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${theme.palette.background.paper}`,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 12, color: 'white' }} />
              </Box>
            )}
          </Box>

          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={700} 
                sx={{ 
                  fontSize: '1rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {creator.display_name || creator.creator}
              </Typography>
              {creator.has_applied && (
                <Chip
                  label="Applied"
                  size="small"
                  color="warning"
                  sx={{ 
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>

            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                fontWeight: 500,
                fontSize: '0.85rem',
              }}
            >
              @{creator.creator}
            </Typography>

            {totalFollowers > 0 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography 
                  variant="caption" 
                  fontWeight={700} 
                  color="primary.main"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {formatNumber(totalFollowers)}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  followers
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </GlassCard>
  );
}

// Memoize component to prevent unnecessary re-renders
export default memo(CreatorSelectionCard, (prevProps, nextProps) => {
  return (
    prevProps.selected === nextProps.selected &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.creator.creator === nextProps.creator.creator &&
    prevProps.creator.has_applied === nextProps.creator.has_applied
  );
});